/**
 * Authentication (docs/08 §7, §10).
 *
 * - `GET /api/auth/me` at start: `authenticated`, `anonymous` (401) or, without network, the last
 *   known user kept on the device (`offline: true`); with no network and no known user the shell
 *   shows the offline screen.
 * - Any 401 of the API (`api:unauthorized`) → `anonymous` → /login with the return path.
 * - With a PIN set on the session, 10 minutes without touches lock the device (`locked` → /lock).
 *   The lock is a convenience for shared tablets: the PIN is verified by the server (5 errors revoke
 *   the session) and the lock survives a reload (localStorage) and is shared by the open tabs.
 *   Reopening the app after the 10 minutes opens /lock: the check runs before the activity
 *   timestamp is refreshed (`shouldStartLocked`).
 * - Logout without network: the HttpOnly cookie is still valid on the server, so the device keeps
 *   `carte:logout-pending` and retries `POST /auth/logout` (at start, back online, every 30 s,
 *   before a new login); until it succeeds the old cookie is never accepted (`decideStartup`).
 * - Login and logout empty the query cache and the API cache of the service worker; the outbox
 *   round of the previous user is stopped before the session cookie changes.
 */
import type { MeResponse, User } from 'core';
import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { OfflineScreen, SplashScreen } from '@/components/layout/StatusScreens';
import { ApiError, UNAUTHORIZED_EVENT, api } from './api';
import { LOCK_AFTER_MS, isLockDue, withNext } from './auth-utils';
import { decideStartup, logoutCompleted } from './auth-session';
import type { AuthStatus } from './auth-session';
import { setOutboxUser, startOutboxSync } from './outbox';
import { clearApiCache } from './api-cache';

export type { AuthStatus } from './auth-session';

/**
 * Why the user is on the login page (message shown there). `logout-pending`: logged out without
 * network, the server still has to close the session.
 */
export type LogoutReason = 'logout' | 'logout-pending' | 'expired' | 'pin';

export interface AuthContextValue {
  status: AuthStatus;
  me: MeResponse | null;
  user: User | null;
  /** `me` comes from the device because the server is not reachable. */
  offline: boolean;
  /** Error of the first `/auth/me` when there is no known user (offline screen). */
  bootError: ApiError | null;
  logoutReason: LogoutReason | null;
  refresh(): Promise<MeResponse | null>;
  login(username: string, password: string): Promise<MeResponse | null>;
  logout(reason?: LogoutReason): Promise<void>;
  changePassword(current: string, next: string): Promise<void>;
  setPin(pin: string): Promise<void>;
  removePin(): Promise<void>;
  /** Locks now (only with a PIN). */
  lock(): void;
  /** Verifies the PIN; throws `ApiError` (`PIN_INVALID` with `details.remainingAttempts`). */
  unlock(pin: string): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const ME_KEY = 'carte:me';
const LOCKED_KEY = 'carte:locked';
const ACTIVITY_KEY = 'carte:last-activity';
/** '1' while a logout done without network still has to reach the server. */
const LOGOUT_PENDING_KEY = 'carte:logout-pending';
const ACTIVITY_WRITE_EVERY_MS = 5_000;
const LOCK_CHECK_EVERY_MS = 15_000;
const LOGOUT_RETRY_EVERY_MS = 30_000;
/** Longest wait for the outbox round of the previous user before the session cookie changes. */
const OUTBOX_STOP_WAIT_MS = 5_000;

/** Waits for `promise` (never rejects), at most `ms`. */
function waitAtMost(promise: Promise<unknown>, ms: number): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    const done = () => {
      clearTimeout(timer);
      resolve();
    };
    promise.then(done, done);
  });
}

const storage = {
  get(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key: string, value: string | null): void {
    try {
      if (value == null) localStorage.removeItem(key);
      else localStorage.setItem(key, value);
    } catch {
      // storage blocked (private mode): the feature degrades, nothing breaks
    }
  },
};

function readCachedMe(): MeResponse | null {
  const raw = storage.get(ME_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as MeResponse;
    return parsed?.user?.id ? parsed : null;
  } catch {
    return null;
  }
}

function lastActivity(): number | null {
  const n = Number(storage.get(ACTIVITY_KEY));
  return Number.isFinite(n) && n > 0 ? n : null;
}

function markActivity(): void {
  storage.set(ACTIVITY_KEY, String(Date.now()));
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [status, setStatusState] = useState<AuthStatus>('unknown');
  const [me, setMe] = useState<MeResponse | null>(null);
  const [offline, setOffline] = useState(false);
  const [bootError, setBootError] = useState<ApiError | null>(null);
  const [logoutReason, setLogoutReason] = useState<LogoutReason | null>(null);
  const [logoutPending, setLogoutPending] = useState(() => storage.get(LOGOUT_PENDING_KEY) === '1');
  // read by event listeners registered once
  const statusRef = useRef<AuthStatus>('unknown');
  const setStatus = useCallback((next: AuthStatus) => {
    statusRef.current = next;
    setStatusState(next);
  }, []);

  /** Opens the session of `next`; `locked` was decided before the activity is refreshed. */
  const applyMe = useCallback(
    (next: MeResponse, fromDevice: boolean, locked: boolean) => {
      setMe(next);
      setOffline(fromDevice);
      setBootError(null);
      if (!fromDevice) storage.set(ME_KEY, JSON.stringify(next));
      void setOutboxUser(next.user.id);
      if (!next.hasPin) storage.set(LOCKED_KEY, null);
      else if (locked) storage.set(LOCKED_KEY, '1');
      setStatus(locked ? 'locked' : 'authenticated');
    },
    [setStatus],
  );

  const clearSession = useCallback(
    (reason: LogoutReason | null) => {
      setMe(null);
      setOffline(false);
      setLogoutReason(reason);
      storage.set(ME_KEY, null);
      storage.set(LOCKED_KEY, null);
      void setOutboxUser(null);
      setStatus('anonymous');
      queryClient.clear();
      void clearApiCache();
    },
    [queryClient, setStatus],
  );

  /**
   * Retries `POST /auth/logout` of a logout done without network. True when nothing is pending
   * any more (done now, or the server answered 401: no session to close).
   */
  const pendingLogoutRef = useRef<Promise<boolean> | null>(null);
  const completePendingLogout = useCallback((): Promise<boolean> => {
    if (storage.get(LOGOUT_PENDING_KEY) !== '1') return Promise.resolve(true);
    pendingLogoutRef.current ??= (async () => {
      let failure: unknown = null;
      try {
        await api.post('/auth/logout');
      } catch (err) {
        failure = err;
      }
      if (!logoutCompleted(failure)) return false;
      storage.set(LOGOUT_PENDING_KEY, null);
      setLogoutPending(false);
      setLogoutReason((prev) => (prev === 'logout-pending' ? 'logout' : prev));
      return true;
    })().finally(() => {
      pendingLogoutRef.current = null;
    });
    return pendingLogoutRef.current;
  }, []);

  const refresh = useCallback(async (): Promise<MeResponse | null> => {
    // a logout still pending is completed before the cookie is trusted again
    if (!(await completePendingLogout())) {
      setLogoutPending(true);
      clearSession('logout-pending');
      return null;
    }
    let next: MeResponse | null = null;
    let meFailure: 'unauthorized' | 'unreachable' | null = null;
    let error: ApiError | null = null;
    try {
      next = await api.get<MeResponse>('/auth/me');
    } catch (err) {
      // 401 is handled by the `api:unauthorized` event
      if (err instanceof ApiError && err.status === 401) meFailure = 'unauthorized';
      else {
        meFailure = 'unreachable';
        error =
          err instanceof ApiError ? err : new ApiError(0, 'NETWORK_ERROR', 'Errore imprevisto');
      }
    }
    const decision = decideStartup({
      logoutPending: storage.get(LOGOUT_PENDING_KEY) === '1',
      me: next,
      meFailure,
      cachedMe: readCachedMe(),
      status: statusRef.current,
      lockedFlag: storage.get(LOCKED_KEY) === '1',
      lastActivityMs: lastActivity(),
      nowMs: Date.now(),
    });
    switch (decision.kind) {
      case 'session':
        // offline: keep working with the last known user (offline PWA)
        applyMe(decision.me, decision.offline, decision.locked);
        return decision.offline ? null : decision.me;
      case 'logged-out':
        setLogoutPending(true);
        clearSession('logout-pending');
        return null;
      case 'boot-error':
        setBootError(error);
        return null;
      case 'keep':
        return null;
    }
  }, [applyMe, clearSession, completePendingLogout]);

  // first load
  useEffect(() => {
    void refresh();
  }, [refresh]);

  // 401 anywhere → back to the login page
  useEffect(() => {
    const onUnauthorized = () => {
      if (statusRef.current === 'anonymous') return;
      // at start a 401 only means "not logged in"; later the session expired or was revoked
      clearSession(statusRef.current === 'unknown' ? null : 'expired');
    };
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, [clearSession]);

  // offline with the cached user: check the session again when the network is back
  useEffect(() => {
    if (!offline && !bootError) return;
    const onOnline = () => void refresh();
    window.addEventListener('online', onOnline);
    const timer = window.setInterval(() => void refresh(), 30_000);
    return () => {
      window.removeEventListener('online', onOnline);
      window.clearInterval(timer);
    };
  }, [offline, bootError, refresh]);

  // logout done without network: close the session on the server as soon as it is reachable
  useEffect(() => {
    if (!logoutPending) return;
    const retry = () => void completePendingLogout();
    window.addEventListener('online', retry);
    const timer = window.setInterval(retry, LOGOUT_RETRY_EVERY_MS);
    return () => {
      window.removeEventListener('online', retry);
      window.clearInterval(timer);
    };
  }, [logoutPending, completePendingLogout]);

  // outbox replay while a user is known (also when the device is locked)
  const userId = me?.user.id ?? null;
  useEffect(() => (userId == null ? undefined : startOutboxSync()), [userId]);

  // inactivity lock
  const hasPin = me?.hasPin ?? false;
  useEffect(() => {
    if (status !== 'authenticated' || !hasPin) return;
    const check = (): boolean => {
      if (!isLockDue(lastActivity(), Date.now(), true, LOCK_AFTER_MS)) return false;
      storage.set(LOCKED_KEY, '1');
      setStatus('locked');
      return true;
    };
    // decided before refreshing the timestamp: opening the app is not a touch
    if (check()) return;
    markActivity();
    let lastWrite = Date.now();
    const onActivity = () => {
      const now = Date.now();
      if (now - lastWrite < ACTIVITY_WRITE_EVERY_MS) return;
      lastWrite = now;
      markActivity();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    const events = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'] as const;
    for (const e of events)
      window.addEventListener(e, onActivity, { passive: true, capture: true });
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(check, LOCK_CHECK_EVERY_MS);
    return () => {
      for (const e of events) window.removeEventListener(e, onActivity, { capture: true });
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, [status, hasPin, setStatus]);

  // lock / unlock / logout done in another tab
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === LOCKED_KEY) {
        if (event.newValue === '1' && statusRef.current === 'authenticated') setStatus('locked');
        if (event.newValue == null && statusRef.current === 'locked') setStatus('authenticated');
      }
      if (event.key === LOGOUT_PENDING_KEY) setLogoutPending(event.newValue === '1');
      if (event.key === ME_KEY && event.newValue == null && statusRef.current !== 'anonymous') {
        void refresh();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [refresh, setStatus]);

  const login = useCallback(
    async (username: string, password: string) => {
      // the previous user's outbox round ends before the cookie changes
      await waitAtMost(setOutboxUser(null), OUTBOX_STOP_WAIT_MS);
      // best effort: close the session of a pending logout while its cookie is still here
      await completePendingLogout();
      await api.post('/auth/login', { username, password });
      // the cookie is now the new session's: the old one is not reachable from this device
      storage.set(LOGOUT_PENDING_KEY, null);
      setLogoutPending(false);
      queryClient.clear();
      await clearApiCache();
      storage.set(LOCKED_KEY, null);
      markActivity();
      setLogoutReason(null);
      return refresh();
    },
    [queryClient, refresh, completePendingLogout],
  );

  const logout = useCallback(
    async (reason: LogoutReason = 'logout') => {
      // no entry of this user leaves while the session is being closed
      await waitAtMost(setOutboxUser(null), OUTBOX_STOP_WAIT_MS);
      let failure: unknown = null;
      try {
        await api.post('/auth/logout');
      } catch (err) {
        failure = err;
      }
      if (logoutCompleted(failure)) {
        storage.set(LOGOUT_PENDING_KEY, null);
        setLogoutPending(false);
        clearSession(reason);
      } else {
        // offline: the HttpOnly cookie is still valid on the server, retried when the network is back
        storage.set(LOGOUT_PENDING_KEY, '1');
        setLogoutPending(true);
        clearSession('logout-pending');
      }
    },
    [clearSession],
  );

  const changePassword = useCallback(async (current: string, next: string) => {
    const result = await api.post<{ user: User }>('/auth/password', { current, next });
    setMe((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, user: result.user };
      storage.set(ME_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const setPin = useCallback(
    async (pin: string) => {
      await api.post('/auth/pin', { pin });
      markActivity();
      await refresh();
    },
    [refresh],
  );

  const removePin = useCallback(async () => {
    await api.delete('/auth/pin');
    storage.set(LOCKED_KEY, null);
    await refresh();
  }, [refresh]);

  const lock = useCallback(() => {
    if (!me?.hasPin) return;
    storage.set(LOCKED_KEY, '1');
    setStatus('locked');
  }, [me?.hasPin, setStatus]);

  const unlock = useCallback(
    async (pin: string) => {
      try {
        await api.post('/auth/pin/verify', { pin });
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) setLogoutReason('pin');
        throw err;
      }
      // activity first: the other tabs unlock on the storage event and must not lock again
      markActivity();
      storage.set(LOCKED_KEY, null);
      setStatus('authenticated');
    },
    [setStatus],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      me,
      user: me?.user ?? null,
      offline,
      bootError,
      logoutReason,
      refresh,
      login,
      logout,
      changePassword,
      setPin,
      removePin,
      lock,
      unlock,
    }),
    [
      status,
      me,
      offline,
      bootError,
      logoutReason,
      refresh,
      login,
      logout,
      changePassword,
      setPin,
      removePin,
      lock,
      unlock,
    ],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve essere usato dentro <AuthProvider>');
  return ctx;
}

/** The logged-in user; only inside `RequireAuth`. */
export function useCurrentUser(): User {
  const { user } = useAuth();
  if (!user) throw new Error('useCurrentUser richiede un utente autenticato');
  return user;
}

/**
 * Guards the authenticated part of the router (wraps AppShell): splash while checking, offline
 * screen without network and known user, /login, /lock or /cambio-password otherwise.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status, me, bootError, refresh } = useAuth();
  const location = useLocation();
  if (status === 'unknown') {
    return bootError ? <OfflineScreen error={bootError} onRetry={refresh} /> : <SplashScreen />;
  }
  if (status === 'anonymous' || !me) return <Navigate to={withNext('/login', location)} replace />;
  if (status === 'locked') return <Navigate to={withNext('/lock', location)} replace />;
  if (me.user.mustChangePassword) {
    return <Navigate to={withNext('/cambio-password', location)} replace />;
  }
  return <>{children}</>;
}
